import { useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { HeaderStyles as S } from '~/app/components/Header/styles'
import logo from '~/assets/images/logo.png'
import { Cohort } from '~/shared/constants/commonConstants'
import { PageId } from '~/shared/constants/pagesConstants'
import { usePageInfoContext } from '~/shared/contexts/PageInfoContext'
import { selectCohortID } from '~/shared/store/common/selects'

const DEFAULT_HEADER_HEIGHT = 53
export const PROGRESS_AREA_HEIGHT = 35
const ANATOMY_HEADER_HEIGHT = DEFAULT_HEADER_HEIGHT + PROGRESS_AREA_HEIGHT
const COMMUNICATION_HEADER_HEIGHT = DEFAULT_HEADER_HEIGHT + 25

const Header = () => {
  const { t } = useTranslation()
  const {
    hasHeader,
    hasProgressbar,
    hasBurgerMenu,
    currentPageId,
    currentPageIndex,
    steps,
    pagesWithProgressBar,
    progressSections,
    currentSectionLabel,
  } = usePageInfoContext()
  const cohort = useSelector(selectCohortID)
  const isCommunication = cohort === Cohort.COMMUNICATION
  const isAnatomyV2 = cohort === Cohort.ANATOMY_V2
  const isGeneral = cohort === Cohort.GENERAL

  const currentStep = steps[currentPageIndex]
  const isSubscriptionLikePage =
    !!currentStep?.isSubscriptions ||
    !!currentStep?.isPayment ||
    !!currentStep?.isUpsell

  const showAnatomyV2BackButton =
    isAnatomyV2 &&
    currentPageId !== PageId.INTRO_ANATOMY_V2_1 &&
    !isSubscriptionLikePage
  const hasColoredBg =
    hasBurgerMenu ||
    (isCommunication && currentPageId === PageId.EMAIL_1) ||
    isGeneral

  const progressStepIndex = pagesWithProgressBar.findIndex(
    ({ id }) => id === currentPageId,
  )

  const progressValue =
    progressStepIndex >= 0 && pagesWithProgressBar.length > 0
      ? ((progressStepIndex + 1) / pagesWithProgressBar.length) * 100
      : 0

  useLayoutEffect(() => {
    if (!hasHeader) return

    let height = DEFAULT_HEADER_HEIGHT
    if (hasProgressbar && progressSections) {
      height = ANATOMY_HEADER_HEIGHT
    } else if (hasProgressbar && isCommunication) {
      height = COMMUNICATION_HEADER_HEIGHT
    }

    document.documentElement.style.setProperty('--header-height', `${height}px`)
  }, [hasHeader, hasProgressbar, progressSections, isCommunication])

  if (!hasHeader) return null

  return (
    <S.Root $hasColoredBg={hasColoredBg}>
      <S.Container>
        <S.HeaderRow>
          <S.Logo src={logo} />
          {(hasProgressbar || showAnatomyV2BackButton) && (
            <S.StyledBackButton />
          )}
        </S.HeaderRow>

        {hasProgressbar && progressSections && (
          <S.ProgressArea>
            <S.SectionLabel>
              {currentSectionLabel
                ? t(currentSectionLabel).toUpperCase()
                : null}
            </S.SectionLabel>
            <S.SegmentsWrapper>
              {progressSections.map(({ label, value, max }) => (
                <S.Segment key={label}>
                  <S.SegmentProgress value={value} max={max} />
                </S.Segment>
              ))}
            </S.SegmentsWrapper>
          </S.ProgressArea>
        )}

        {hasProgressbar && !progressSections && isCommunication && (
          <>
            <S.CommunicationProgress max={100} value={progressValue} />
            <S.CommunicationDivider />
          </>
        )}
      </S.Container>

      {hasProgressbar &&
        !progressSections &&
        !isCommunication &&
        !isAnatomyV2 && <S.Progress max={100} value={progressValue} />}
    </S.Root>
  )
}

export default Header
